/**
 * Sentry error tracking — graceful no-op when VITE_SENTRY_DSN is not set.
 *
 * Usage:
 *   import { initSentry, setSentryUser, clearSentryUser, captureException } from '@/lib/sentry';
 *   initSentry();                                    // call once in main.tsx
 *   setSentryUser({ id, email, role });               // call on login
 *   clearSentryUser();                                // call on logout
 *   captureException(error, { extra: { jobId } });    // manual capture
 */

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.VITE_APP_ENV as string) || (import.meta.env.DEV ? 'development' : 'production');
const enabled = !!DSN;

export function initSentry() {
  if (!enabled) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] Disabled — VITE_SENTRY_DSN not set');
    }
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    // Only capture 10% of transactions for performance monitoring
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    // Session replays: only on errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Don't send PII by default
    sendDefaultPii: false,
    // Ignore common noise
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Network request failed',
      'Load failed',
      'Failed to fetch',
    ],
  });
}

export function setSentryUser(user: { id: string; email?: string; role?: string }) {
  if (!enabled) return;
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  } as Sentry.User);
}

export function clearSentryUser() {
  if (!enabled) return;
  Sentry.setUser(null);
}

export function captureException(
  error: unknown,
  context?: { extra?: Record<string, unknown>; tags?: Record<string, string> }
) {
  if (!enabled) {
    console.error('[Sentry disabled] Uncaptured exception:', error);
    return;
  }
  Sentry.captureException(error, {
    extra: context?.extra,
    tags: context?.tags,
  });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (!enabled) return;
  Sentry.captureMessage(message, level);
}

export { Sentry };
